import React, { useState, useMemo } from 'react';
import { Product } from '../../types/product';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { XMarkIcon, DocumentArrowDownIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import PriceListPrintPreview from './PriceListPrintPreview';

// pdfMake fontok beállítása
(pdfMake as any).vfs = pdfFonts;

interface PriceListGeneratorProps {
  products: Product[];
  productInventorySummary: Record<string, { total: number; available: number }>;
  onClose: () => void;
}

const PriceListGenerator: React.FC<PriceListGeneratorProps> = ({
  products,
  productInventorySummary,
  onClose
}) => {
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set(products.map(p => p.id)));
  const [includeStock, setIncludeStock] = useState(true);
  const [includeSpecs, setIncludeSpecs] = useState(true);
  const [includeImages, setIncludeImages] = useState(true);
  const [title, setTitle] = useState('Termék Katalógus');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  // Kategóriák kinyerése
  const categories = useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => {
      if (p.category?.level1) {
        cats.add(p.category.level1);
      }
    });
    return Array.from(cats).sort();
  }, [products]);

  // Szűrt termékek kategória szerint
  const filteredProducts = useMemo(() => {
    if (selectedCategories.size === 0) return products;
    return products.filter(p => 
      p.category?.level1 && selectedCategories.has(p.category.level1)
    );
  }, [products, selectedCategories]);

  const handleSelectAll = () => {
    const filteredIds = filteredProducts.map(p => p.id);
    const allFilteredSelected = filteredIds.every(id => selectedProducts.has(id));
    
    if (allFilteredSelected) {
      // Töröljük a szűrt termékeket a kijelölésből
      const newSelected = new Set(selectedProducts);
      filteredIds.forEach(id => newSelected.delete(id));
      setSelectedProducts(newSelected);
    } else {
      // Hozzáadjuk az összes szűrt terméket
      const newSelected = new Set(selectedProducts);
      filteredIds.forEach(id => newSelected.add(id));
      setSelectedProducts(newSelected);
    }
  };

  const handleToggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleToggleCategory = (category: string) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const handleSelectAllCategories = () => {
    if (selectedCategories.size === categories.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(categories));
    }
  };

  // Kép URL konvertálása base64-re Firebase Cloud Function használatával
  const getBase64ImageFromUrl = async (imageUrl: string): Promise<string> => {
    try {
      const functions = getFunctions();
      // Production használata emulator helyett
      // connectFunctionsEmulator(functions, 'localhost', 5001);
      const getImageAsBase64 = httpsCallable(functions, 'getImageAsBase64');
      
      const result = await getImageAsBase64({ imageUrl });
      const data = result.data as any;
      
      if (data.success && data.dataUrl) {
        return data.dataUrl;
      } else {
        console.error('Failed to fetch image:', imageUrl);
        return '';
      }
    } catch (error) {
      console.error('Error loading image via Cloud Function:', imageUrl, error);
      return '';
    }
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const selectedProductsList = filteredProducts.filter(p => selectedProducts.has(p.id));

    // Kategória szerinti csoportosítás
    const groupedProducts: Record<string, Product[]> = {};
    if (groupByCategory) {
      selectedProductsList.forEach(product => {
        const category = product.category?.level1 || 'Kategória nélkül';
        if (!groupedProducts[category]) {
          groupedProducts[category] = [];
        }
        groupedProducts[category].push(product);
      });
    } else {
      groupedProducts['Összes termék'] = selectedProductsList;
    }

    // Képek betöltése base64 formátumban (batch mode)
    const productImages: Record<string, string> = {};
    let loadedImagesCount = 0;
    if (includeImages) {
      const productsWithImages = selectedProductsList.filter(p => p.thumbnailUrl);
      const totalImages = productsWithImages.length;
      
      if (totalImages > 0) {
        try {
          // Batch letöltés Firebase Cloud Function-nel
          const functions = getFunctions();
          // Emulator használata localhost-on
          if (window.location.hostname === 'localhost') {
            connectFunctionsEmulator(functions, 'localhost', 5001);
          }
          const getImagesAsBase64 = httpsCallable(functions, 'getImagesAsBase64');
          
          const imageUrls = productsWithImages.map(p => p.thumbnailUrl!);
          const result = await getImagesAsBase64({ imageUrls });
          const data = result.data as any;
          
          if (data.success && data.images) {
            data.images.forEach((img: any, index: number) => {
              if (img.success && img.dataUrl) {
                const product = productsWithImages[index];
                productImages[product.id] = img.dataUrl;
                loadedImagesCount++;
              }
            });
          }
          
          console.log(`Betöltött képek: ${loadedImagesCount} / ${totalImages}`);
          
          if (loadedImagesCount === 0 && totalImages > 0) {
            alert('⚠️ A képek nem töltődtek be.\n\nA PDF képek nélkül fog generálódni.\n\nEllenőrizd a Firebase Cloud Function-t!');
          } else if (loadedImagesCount < totalImages) {
            alert(`⚠️ Csak ${loadedImagesCount} / ${totalImages} kép töltődött be.\n\nNéhány kép hiányozni fog a PDF-ből.`);
          }
        } catch (error) {
          console.error('Error loading images:', error);
          alert('⚠️ Hiba történt a képek betöltése során.\n\nA PDF képek nélkül fog generálódni.');
        }
      }
    }

    // PDF tartalom építése kategóriánként
    const pdfContent: any[] = [];

    Object.keys(groupedProducts).sort().forEach((category, catIndex) => {
      const categoryProducts = groupedProducts[category];
      
      // Kategória címsor (ha csoportosítva van)
      if (groupByCategory && Object.keys(groupedProducts).length > 1) {
        if (catIndex > 0) {
          pdfContent.push({ text: '', pageBreak: 'before' }); // Új oldal minden kategóriánál
        }
        pdfContent.push({
          text: category,
          style: 'categoryHeader',
          margin: [0, 0, 0, 15]
        });
      }

      // Katalógus stílus (képekkel) vagy táblázat stílus
      if (includeImages) {
        // Katalógus stílus - termékek kártyaként
        categoryProducts.forEach((product, index) => {
          const productAny = product as any;
          const specs: string[] = [];
          
          if (includeSpecs && productAny.specifications) {
            if (productAny.specifications.cpu) specs.push(productAny.specifications.cpu);
            if (productAny.specifications.ram) specs.push(productAny.specifications.ram);
            if (productAny.specifications.storage) specs.push(productAny.specifications.storage);
            if (productAny.specifications.screen_size) specs.push(productAny.specifications.screen_size);
            if (productAny.specifications.graphics) specs.push(productAny.specifications.graphics);
            if (productAny.specifications.os) specs.push(productAny.specifications.os);
          }

          const inventory = productInventorySummary[product.id];
          
          const productCard: any = {
            margin: [0, index > 0 ? 15 : 0, 0, 0],
            table: {
              widths: [120, '*'],
              body: [
                [
                  // Kép
                  productImages[product.id] ? {
                    image: productImages[product.id],
                    width: 110,
                    height: 110,
                    margin: [5, 5, 5, 5]
                  } : {
                    text: 'Nincs kép',
                    alignment: 'center',
                    margin: [5, 45, 5, 45],
                    color: '#9CA3AF'
                  },
                  // Termék adatok
                  {
                    stack: [
                      { text: product.name || '-', style: 'productName', margin: [0, 0, 0, 5] },
                      {
                        columns: [
                          {
                            width: '*',
                            stack: [
                              { text: 'Nettó ár:', style: 'label' },
                              { 
                                text: product.netPrice ? `${product.netPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-',
                                style: 'price',
                                margin: [0, 0, 0, 5]
                              }
                            ]
                          },
                          {
                            width: '*',
                            stack: [
                              { text: 'Bruttó ár:', style: 'label' },
                              { 
                                text: product.grossPrice ? `${product.grossPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-',
                                style: 'priceGross',
                                margin: [0, 0, 0, 5]
                              }
                            ]
                          }
                        ]
                      },
                      ...(includeStock ? [{
                        text: `Készlet: ${inventory ? `${inventory.available} db` : '0 db'}`,
                        style: 'stock',
                        margin: [0, 0, 0, 5]
                      }] : []),
                      ...(specs.length > 0 ? [{
                        text: specs.join(' • '),
                        style: 'specs',
                        margin: [0, 5, 0, 0]
                      }] : [])
                    ],
                    margin: [10, 5, 5, 5]
                  }
                ]
              ]
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => '#E5E7EB',
              vLineColor: () => '#E5E7EB',
              paddingLeft: () => 0,
              paddingRight: () => 0,
              paddingTop: () => 0,
              paddingBottom: () => 0
            }
          };
          
          pdfContent.push(productCard);
        });
      } else {
        // Táblázat stílus (eredeti)
        const tableHeaders = ['Terméknév', 'Nettó ár', 'Bruttó ár'];
        if (includeStock) {
          tableHeaders.push('Készlet');
        }
        if (includeSpecs) {
          tableHeaders.push('Specifikáció');
        }

        const tableBody = categoryProducts.map(product => {
          const row: any[] = [
            product.name || '-',
            product.netPrice ? `${product.netPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-',
            product.grossPrice ? `${product.grossPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-'
          ];

          if (includeStock) {
            const inventory = productInventorySummary[product.id];
            row.push(inventory ? `${inventory.available} db` : '0 db');
          }

          if (includeSpecs) {
            const specs: string[] = [];
            const productAny = product as any;
            
            if (productAny.specifications) {
              if (productAny.specifications.cpu) specs.push(productAny.specifications.cpu);
              if (productAny.specifications.ram) specs.push(productAny.specifications.ram);
              if (productAny.specifications.storage) specs.push(productAny.specifications.storage);
              if (productAny.specifications.screen_size) specs.push(productAny.specifications.screen_size);
              if (productAny.specifications.graphics) specs.push(productAny.specifications.graphics);
              if (productAny.specifications.os) specs.push(productAny.specifications.os);
            }
            
            row.push(specs.length > 0 ? specs.join(', ') : '-');
          }

          return row;
        });

        pdfContent.push({
          table: {
            headerRows: 1,
            widths: includeSpecs 
              ? (includeStock ? ['*', 60, 60, 50, '*'] : ['*', 60, 60, '*'])
              : (includeStock ? ['*', 60, 60, 50] : ['*', 60, 60]),
            body: [
              tableHeaders.map(h => ({ text: h, style: 'tableHeader' })),
              ...tableBody
            ]
          },
          layout: {
            fillColor: (rowIndex: number) => {
              return rowIndex === 0 ? '#4F46E5' : (rowIndex % 2 === 0 ? '#F9FAFB' : null);
            },
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#E5E7EB',
            vLineColor: () => '#E5E7EB'
          }
        });
      }
    });

    const docDefinition: TDocumentDefinitions = {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [40, 60, 40, 60],
      header: {
        margin: [40, 20, 40, 0],
        columns: [
          {
            text: title,
            style: 'header',
            alignment: 'left'
          },
          {
            text: format(new Date(), 'yyyy. MM. dd.'),
            style: 'date',
            alignment: 'right'
          }
        ]
      },
      footer: (currentPage: number, pageCount: number) => {
        return {
          margin: [40, 0, 40, 20],
          columns: [
            {
              text: 'Készült a YouCRM rendszerrel',
              style: 'footer',
              alignment: 'left'
            },
            {
              text: `${currentPage} / ${pageCount}`,
              style: 'footer',
              alignment: 'right'
            }
          ]
        };
      },
      content: pdfContent,
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#1F2937'
        },
        date: {
          fontSize: 10,
          color: '#6B7280'
        },
        tableHeader: {
          bold: true,
          fontSize: 11,
          color: 'white',
          fillColor: '#4F46E5',
          alignment: 'center'
        },
        categoryHeader: {
          fontSize: 16,
          bold: true,
          color: '#4F46E5',
          margin: [0, 10, 0, 5]
        },
        productName: {
          fontSize: 12,
          bold: true,
          color: '#1F2937'
        },
        label: {
          fontSize: 8,
          color: '#6B7280',
          margin: [0, 0, 0, 2]
        },
        price: {
          fontSize: 11,
          bold: true,
          color: '#059669'
        },
        priceGross: {
          fontSize: 11,
          bold: true,
          color: '#DC2626'
        },
        stock: {
          fontSize: 9,
          color: '#4B5563'
        },
        specs: {
          fontSize: 8,
          color: '#6B7280',
          italics: true
        },
        footer: {
          fontSize: 8,
          color: '#6B7280'
        }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#374151'
      }
    };

      pdfMake.createPdf(docDefinition).download(`arlista_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Hiba történt a PDF generálása során. Kérlek próbáld újra!');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Árlista generálás</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Beállítások */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Árlista címe
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Kategória szűrés ({selectedCategories.size} / {categories.length})
                </label>
                <button
                  onClick={handleSelectAllCategories}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {selectedCategories.size === categories.length ? 'Kijelölés törlése' : 'Összes kijelölése'}
                </button>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                {categories.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    Nincsenek kategóriák
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(cat => (
                      <label
                        key={cat}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(cat)}
                          onChange={() => handleToggleCategory(cat)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{cat}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCategories.size === 0 && (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  ℹ️ Nincs kategória kiválasztva - minden termék megjelenik
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeImages}
                  onChange={(e) => setIncludeImages(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">📸 Képek megjelenítése (Katalógus)</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={groupByCategory}
                  onChange={(e) => setGroupByCategory(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Csoportosítás kategóriák szerint</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStock}
                  onChange={(e) => setIncludeStock(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Készlet megjelenítése</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSpecs}
                  onChange={(e) => setIncludeSpecs(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Specifikáció megjelenítése</span>
              </label>
            </div>
          </div>

          {/* Termékek kiválasztása */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Termékek ({selectedProducts.size} / {filteredProducts.length})
              </h3>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {filteredProducts.every(p => selectedProducts.has(p.id)) ? 'Kijelölés törlése' : 'Összes kijelölése'}
              </button>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-96 overflow-y-auto">
              {filteredProducts.map(product => (
                <label
                  key={product.id}
                  className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(product.id)}
                    onChange={() => handleToggleProduct(product.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {product.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {product.code} • {product.netPrice ? `${product.netPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : 'Nincs ár'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Mégse
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPrintPreview(true)}
              disabled={selectedProducts.size === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
              <PrinterIcon className="w-5 h-5" />
              Nyomtatási előnézet
            </button>
            <button
              onClick={generatePDF}
              disabled={selectedProducts.size === 0 || isGenerating}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
            {isGenerating ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Képek betöltése...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="w-5 h-5" />
                PDF letöltése
              </>
            )}
          </button>
          </div>
        </div>
      </div>
    </div>

    {/* Print Preview Modal */}
    {showPrintPreview && (
      <PriceListPrintPreview
        products={filteredProducts.filter(p => selectedProducts.has(p.id))}
        productInventorySummary={productInventorySummary}
        title={title}
        includeStock={includeStock}
        includeSpecs={includeSpecs}
        includeImages={includeImages}
        groupByCategory={groupByCategory}
        onClose={() => setShowPrintPreview(false)}
      />
    )}
    </>
  );
};

export default PriceListGenerator;
